import { FC, useMemo } from 'react';
import { Line, LineChart, ResponsiveContainer, YAxis } from 'recharts';

import type { ActivityCounts, TeamActivityResponse } from '@/api/gamma-team/types';
import { theme, Typography } from '@/design-system';

import { CHART_REFERENCE } from './chart-palette';
import { formatActivityRange } from './period';
import { ChangeText, KpiGrid, KpiTile, KpiValueRow, SkeletonBar, Sparkline } from './styled';
import { compareCounts, countActiveMembers, formatPerDay } from './team-trends';

type KpiMetric = keyof ActivityCounts | 'activeMembers';

export const KPI_METRICS: { key: KpiMetric; label: string }[] = [
  { key: 'contactsReached', label: 'Contacts reached' },
  { key: 'entries', label: 'Entries' },
  { key: 'dealsCreated', label: 'Deals created' },
  { key: 'dealsAdvanced', label: 'Deals advanced' },
  { key: 'activeMembers', label: 'Active members' },
];

const SPARK_DOT_RADIUS = 3;

interface SparkProps {
  values: number[];
}

const Spark: FC<SparkProps> = ({ values }) => {
  const data = values.map((value, index) => ({ index, value }));
  const last = data.length - 1;
  return (
    <Sparkline aria-hidden>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
          <YAxis hide domain={[0, 'dataMax']} />
          <Line
            type="monotone"
            dataKey="value"
            stroke={CHART_REFERENCE}
            strokeWidth={2}
            isAnimationActive={false}
            dot={(props: { cx?: number; cy?: number; index?: number }) =>
              props.index === last && props.cx !== undefined && props.cy !== undefined ? (
                <circle
                  key="end"
                  cx={props.cx}
                  cy={props.cy}
                  r={SPARK_DOT_RADIUS}
                  fill={theme.colors.brand.solid}
                  stroke={theme.colors.background.raised}
                  strokeWidth={2}
                />
              ) : (
                <g key={props.index} />
              )
            }
          />
        </LineChart>
      </ResponsiveContainer>
    </Sparkline>
  );
};

interface TeamKpisProps {
  data: TeamActivityResponse | undefined;
}

export const TeamKpis: FC<TeamKpisProps> = ({ data }) => {
  const tiles = useMemo(() => {
    if (!data) return null;
    const daily = data.daily ?? [];
    const previousLabel = data.previous ? formatActivityRange(data.previous.range) : '';
    const activeMembers = countActiveMembers(data.members);

    return KPI_METRICS.map(({ key, label }) => {
      const series = daily.map((day) => day[key]);
      if (key === 'activeMembers') {
        const single = data.members.length === 1;
        const activeDays = daily.filter((day) => day.activeMembers > 0).length;
        const current = single ? activeDays : activeMembers;
        const previous = single ? data.previous?.activeDays : data.previous?.activeMembers;
        return {
          key,
          label: single ? 'Active days' : label,
          value: single
            ? `${activeDays} / ${daily.length}`
            : `${activeMembers} / ${data.members.length}`,
          caption: single ? 'Days with any activity' : 'Logged an entry or moved a deal',
          change: previous === undefined ? null : compareCounts(current, previous, previousLabel),
          series,
        };
      }
      const current = data.total[key];
      return {
        key,
        label,
        value: current.toLocaleString(),
        caption: formatPerDay(current, daily.length) ?? 'Nothing logged yet',
        change: data.previous
          ? compareCounts(current, data.previous.total[key], previousLabel)
          : null,
        series,
      };
    });
  }, [data]);

  return (
    <KpiGrid role="list" aria-label="Key figures">
      {(tiles ?? KPI_METRICS.map(({ key, label }) => ({ key, label, value: null }))).map((tile) => (
        <KpiTile key={tile.key} role="listitem">
          <Typography variant="body3" color={theme.colors.text.secondary} noWrap>
            {tile.label}
          </Typography>
          {tile.value === null || !('series' in tile) ? (
            <SkeletonBar w="60%" h={28} />
          ) : (
            <>
              <KpiValueRow>
                <Typography variant="h4" color={theme.colors.text.primary}>
                  {tile.value}
                </Typography>
                {tile.change && (
                  <ChangeText
                    tone={tile.change.tone}
                    title={tile.change.description}
                    aria-label={tile.change.description}
                  >
                    {tile.change.label}
                  </ChangeText>
                )}
              </KpiValueRow>
              <Typography variant="body4" color={theme.colors.text.secondary} noWrap>
                {tile.caption}
              </Typography>
              {tile.series.length > 1 && <Spark values={tile.series} />}
            </>
          )}
        </KpiTile>
      ))}
    </KpiGrid>
  );
};
